import WarningIcon from '@mui/icons-material/Warning'
import { Box, Button, CircularProgress, Popover, Typography, useTheme } from '@mui/material'
import { FC, useState } from 'react'
import { DeleteConfirmPopoverProps } from './types'

/**
 * A reusable delete confirmation popover.
 * Displays a warning with entity preview and confirm/cancel buttons.
 *
 * @example
 * // Single item delete
 * <DeleteConfirmPopover
 *   open={isDeleteOpen}
 *   onClose={handleClose}
 *   onConfirm={handleDelete}
 *   entityName="Contact"
 *   entityIcon={<PersonIcon sx={{ color: 'rgb(255, 123, 187)' }} />}
 *   selectedData={selectedContact}
 *   renderPreview={(contact) => contact.name}
 * />
 *
 * // Batch delete
 * <DeleteConfirmPopover
 *   open={isBatchDeleteOpen}
 *   onClose={handleClose}
 *   onConfirm={handleBatchDelete}
 *   entityName="Lead"
 *   selectionCount={5}
 * />
 */
const DeleteConfirmPopover: FC<DeleteConfirmPopoverProps> = ({
  id,
  open,
  anchorEl,
  onClose,
  onConfirm,
  selectedData,
  selectedItems,
  selectionCount,
  entityName,
  entityNamePlural,
  entityIcon,
  renderPreview,
  renderBatchPreview,
  getItemName = (item: any) => item?.name ?? item?.title ?? 'Unknown',
  getItemKey = (item: any) => item?.id ?? item?.PK ?? item?.SK,
  isDeleting: externalIsDeleting,
  setIsDeleting: externalSetIsDeleting,
  manageLoadingState = true,
  anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
  transformOrigin = { vertical: 'top', horizontal: 'center' },
  paperSx,
  contentSx,
}) => {
  const theme = useTheme()
  const [internalIsDeleting, setInternalIsDeleting] = useState(false)

  // Use external or internal loading state
  const isDeleting = externalIsDeleting ?? internalIsDeleting
  const setIsDeleting = externalSetIsDeleting ?? setInternalIsDeleting

  // Determine if this is a batch delete
  const isBatchDelete = (selectedItems && selectedItems.length > 1) || (selectionCount && selectionCount > 1)
  const itemCount = selectionCount ?? selectedItems?.length ?? (selectedData ? 1 : 0)
  const plural = entityNamePlural ?? `${entityName}s`

  const handleConfirm = async () => {
    if (manageLoadingState) {
      setIsDeleting(true)
    }
    try {
      await onConfirm()
    } finally {
      if (manageLoadingState) {
        setIsDeleting(false)
      }
    }
  }

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        root: {
          sx: {
            '& .MuiBackdrop-root': {
              backgroundColor: 'background.defaultdim',
              zIndex: 1800,
            },
            zIndex: 1800,
          },
        },
        paper: {
          sx: {
            zIndex: 1801,
            ...paperSx,
          },
        },
      }}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={{
        backgroundColor:
          theme.palette.mode === 'dark' ? 'rgba(30, 39, 53, 0.85)' : 'rgba(255, 255, 255, 0.85)',
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          padding: theme.spacing(2),
          minWidth: 300,
          maxWidth: 400,
          ...contentSx,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: '100%' }}>
          <WarningIcon sx={{ color: 'crimson' }} />
          <Typography sx={{ fontWeight: 'bold', color: 'crimson' }}>Confirm Deletion</Typography>
        </Box>

        {/* Description */}
        <Typography sx={{ mb: 2, textAlign: 'center' }}>
          Are you sure you want to permanently delete{' '}
          {isBatchDelete ? (
            <>
              <strong>{itemCount}</strong> {plural}
            </>
          ) : (
            `this ${entityName}`
          )}{' '}
          from Grobotic?
        </Typography>

        {/* Single item preview */}
        {selectedData && !isBatchDelete && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              width: '100%',
              border: '1px solid',
              borderColor: 'rgba(201, 50, 46, 0.5)',
              padding: theme.spacing(1),
              borderRadius: 1,
              mb: 2,
            }}
          >
            {entityIcon}
            <Typography
              sx={{
                ml: entityIcon ? 1 : 0,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {renderPreview ? renderPreview(selectedData) : getItemName(selectedData)}
            </Typography>
          </Box>
        )}

        {/* Batch preview */}
        {isBatchDelete && selectedItems && renderBatchPreview && (
          <Box sx={{ width: '100%', mb: 2 }}>{renderBatchPreview(selectedItems)}</Box>
        )}

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            marginTop: theme.spacing(1),
            gap: theme.spacing(1),
          }}
        >
          <Button
            variant="outlined"
            onClick={handleConfirm}
            disabled={isDeleting}
            sx={{
              color: 'crimson',
              borderColor: 'crimson',
              '&:hover': {
                borderColor: 'darkred',
                backgroundColor: 'rgba(220, 20, 60, 0.08)',
              },
            }}
          >
            {isDeleting ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
          </Button>
          <Button variant="contained" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Popover>
  )
}

export default DeleteConfirmPopover
